/**
 * @file departmentService.ts
 * @brief Department list, detail, comparison, performance, task distribution, member workload, and teams.
 * Uses backend API for all operations; no mock data.
 */

import apiClient, { unwrapResponse } from './apiClient';
import { API_ENDPOINTS } from './endpoints';
import type {
  ApiResponse,
  DepartmentSummary,
  DepartmentPerformance,
  DepartmentComparison,
  DepartmentTaskDistribution,
  DepartmentMemberWorkload,
  DepartmentTeamMetrics,
  PerformancePeriod,
  UserRole,
} from '@/types';

function mapRawToDepartmentSummary(raw: Record<string, unknown>): DepartmentSummary {
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    headId: String(raw.headId ?? ''),
    headName: (raw.headName as string) ?? '',
    teamCount: Number(raw.teamCount ?? 0),
    memberCount: Number(raw.memberCount ?? 0),
    activeProjects: Number(raw.activeProjects ?? 0),
    totalTasks: Number(raw.totalTasks ?? 0),
    color: raw.color != null ? String(raw.color) : undefined,
  };
}

function mapRawToTeamMetrics(raw: Record<string, unknown>): DepartmentTeamMetrics {
  return {
    teamId: String(raw.teamId ?? raw.id ?? ''),
    teamName: String(raw.teamName ?? raw.name ?? ''),
    leadId: String(raw.leadId ?? ''),
    leadName: String(raw.leadName ?? ''),
    memberCount: Number(raw.memberCount ?? 0),
    activeTasks: Number(raw.activeTasks ?? 0),
    completedTasks: Number(raw.completedTasks ?? 0),
    avgPerformance: Number(raw.avgPerformance ?? 0),
    avgUtilization: Number(raw.avgUtilization ?? 0),
    velocity: Number(raw.velocity ?? 0),
  };
}

function mapRawToPerformance(raw: Record<string, unknown>): DepartmentPerformance {
  const teams = Array.isArray(raw.teams)
    ? (raw.teams as Record<string, unknown>[]).map(mapRawToTeamMetrics)
    : [];
  const trend = Array.isArray(raw.trend) ? (raw.trend as Record<string, unknown>[]) : [];
  return {
    departmentId: String(raw.departmentId ?? ''),
    departmentName: String(raw.departmentName ?? ''),
    period: {
      type: (raw.period as Record<string, unknown>)?.type as PerformancePeriod ?? 'month',
      startDate: String((raw.period as Record<string, unknown>)?.startDate ?? ''),
      endDate: String((raw.period as Record<string, unknown>)?.endDate ?? ''),
    },
    totalTasks: Number(raw.totalTasks ?? 0),
    completedTasks: Number(raw.completedTasks ?? 0),
    inProgressTasks: Number(raw.inProgressTasks ?? 0),
    notStartedTasks: Number(raw.notStartedTasks ?? 0),
    blockedTasks: Number(raw.blockedTasks ?? 0),
    overdueTasks: Number(raw.overdueTasks ?? 0),
    completionRate: Number(raw.completionRate ?? 0),
    onTimeDeliveryRate: Number(raw.onTimeDeliveryRate ?? 0),
    totalMembers: Number(raw.totalMembers ?? 0),
    activeMembers: Number(raw.activeMembers ?? 0),
    avgUtilization: Number(raw.avgUtilization ?? 0),
    overloadedMembers: Number(raw.overloadedMembers ?? 0),
    underutilizedMembers: Number(raw.underutilizedMembers ?? 0),
    avgHitRate: Number(raw.avgHitRate ?? 0),
    avgVelocity: Number(raw.avgVelocity ?? 0),
    totalEstimatedHours: Number(raw.totalEstimatedHours ?? 0),
    totalActualHours: Number(raw.totalActualHours ?? 0),
    activeProjects: Number(raw.activeProjects ?? 0),
    completedProjects: Number(raw.completedProjects ?? 0),
    projectsAtRisk: Number(raw.projectsAtRisk ?? 0),
    avgProjectHealth: Number(raw.avgProjectHealth ?? 0),
    teams,
    trend: trend.map((t: Record<string, unknown>) => ({
      date: String(t.date ?? ''),
      completionRate: Number(t.completionRate ?? 0),
      velocity: Number(t.velocity ?? 0),
      utilization: Number(t.utilization ?? 0),
    })),
  };
}

const wrapResponse = <T>(data: T): ApiResponse<T> => ({
  data,
  success: true,
  message: 'Success',
});

export const departmentService = {
  /**
   * Get all departments from backend. Returns unwrapped DepartmentSummary[].
   */
  async getDepartments(): Promise<DepartmentSummary[]> {
    const response = await apiClient.get(API_ENDPOINTS.DEPARTMENTS.LIST);
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapRawToDepartmentSummary) : [];
  },

  /**
   * Get a single department by ID from backend. Returns unwrapped DepartmentSummary or null.
   */
  async getDepartment(departmentId: string): Promise<DepartmentSummary | null> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.DEPARTMENTS.GET(departmentId));
      const data = unwrapResponse<Record<string, unknown>>(response);
      return data ? mapRawToDepartmentSummary(data) : null;
    } catch {
      return null;
    }
  },

  /**
   * Get department performance analytics from backend.
   */
  async getDepartmentPerformance(
    departmentId: string,
    period: PerformancePeriod = 'month'
  ): Promise<ApiResponse<DepartmentPerformance>> {
    const response = await apiClient.get(
      `${API_ENDPOINTS.DEPARTMENTS.PERFORMANCE(departmentId)}?period=${encodeURIComponent(period)}`
    );
    const data = unwrapResponse<Record<string, unknown>>(response);
    return wrapResponse(mapRawToPerformance(data));
  },

  /**
   * Get department comparison across all departments from backend.
   */
  async getDepartmentsComparison(
    period: PerformancePeriod = 'month'
  ): Promise<ApiResponse<DepartmentComparison>> {
    const response = await apiClient.get(
      `${API_ENDPOINTS.DEPARTMENTS.COMPARISON}?period=${encodeURIComponent(period)}`
    );
    const data = unwrapResponse<Record<string, unknown>>(response);
    const departments = Array.isArray(data.departments)
      ? (data.departments as Record<string, unknown>[])
      : [];
    return wrapResponse({
      departments: departments.map((d: Record<string, unknown>) => ({
        departmentId: String(d.departmentId ?? ''),
        departmentName: String(d.departmentName ?? ''),
        memberCount: Number(d.memberCount ?? 0),
        completionRate: Number(d.completionRate ?? 0),
        onTimeRate: Number(d.onTimeRate ?? 0),
        utilization: Number(d.utilization ?? 0),
        velocity: Number(d.velocity ?? 0),
        hitRate: Number(d.hitRate ?? 0),
        projectCount: Number(d.projectCount ?? 0),
        riskLevel: (d.riskLevel as 'low' | 'medium' | 'high') ?? 'low',
      })),
      period: (data.period as PerformancePeriod) ?? 'month',
      periodLabel: String(data.periodLabel ?? 'Month'),
    });
  },

  /**
   * Get task distribution for a department from backend.
   */
  async getDepartmentTaskDistribution(
    departmentId: string
  ): Promise<ApiResponse<DepartmentTaskDistribution>> {
    const response = await apiClient.get(API_ENDPOINTS.DEPARTMENTS.TASK_DISTRIBUTION(departmentId));
    const data = unwrapResponse<Record<string, unknown>>(response);
    const tasksByTeam = Array.isArray(data.tasksByTeam)
      ? (data.tasksByTeam as Record<string, unknown>[]).map((t: Record<string, unknown>) => ({
          teamId: String(t.teamId ?? ''),
          teamName: String(t.teamName ?? ''),
          taskCount: Number(t.taskCount ?? 0),
          completedCount: Number(t.completedCount ?? 0),
        }))
      : [];
    return wrapResponse({
      departmentId: String(data.departmentId ?? ''),
      departmentName: String(data.departmentName ?? ''),
      tasksByPriority: {
        p1: Number((data.tasksByPriority as Record<string, unknown>)?.p1 ?? 0),
        p2: Number((data.tasksByPriority as Record<string, unknown>)?.p2 ?? 0),
        p3: Number((data.tasksByPriority as Record<string, unknown>)?.p3 ?? 0),
        p4: Number((data.tasksByPriority as Record<string, unknown>)?.p4 ?? 0),
      },
      tasksByStatus: {
        notStarted: Number((data.tasksByStatus as Record<string, unknown>)?.notStarted ?? 0),
        inProgress: Number((data.tasksByStatus as Record<string, unknown>)?.inProgress ?? 0),
        blocked: Number((data.tasksByStatus as Record<string, unknown>)?.blocked ?? 0),
        completed: Number((data.tasksByStatus as Record<string, unknown>)?.completed ?? 0),
        cancelled: Number((data.tasksByStatus as Record<string, unknown>)?.cancelled ?? 0),
      },
      tasksByTeam,
    });
  },

  /**
   * Get member workload for a department from backend.
   */
  async getDepartmentMemberWorkload(
    departmentId: string
  ): Promise<ApiResponse<DepartmentMemberWorkload>> {
    const response = await apiClient.get(API_ENDPOINTS.DEPARTMENTS.MEMBER_WORKLOAD(departmentId));
    const data = unwrapResponse<Record<string, unknown>>(response);
    const members = Array.isArray(data.members)
      ? (data.members as Record<string, unknown>[]).map((m: Record<string, unknown>) => ({
          userId: String(m.userId ?? ''),
          userName: String(m.userName ?? ''),
          teamId: String(m.teamId ?? ''),
          teamName: String(m.teamName ?? ''),
          role: m.role as UserRole,
          utilizationPercent: Number(m.utilizationPercent ?? 0),
          allocatedHours: Number(m.allocatedHours ?? 0),
          loggedHours: Number(m.loggedHours ?? 0),
          totalAvailableHours: Number(m.totalAvailableHours ?? 40),
          activeTasks: Number(m.activeTasks ?? 0),
          isOverloaded: Boolean(m.isOverloaded),
          performanceRating: Number(m.performanceRating ?? 0),
        }))
      : [];
    const summary = data.summary as Record<string, unknown> | undefined;
    return wrapResponse({
      departmentId: String(data.departmentId ?? ''),
      departmentName: String(data.departmentName ?? ''),
      members,
      summary: {
        totalMembers: Number(summary?.totalMembers ?? 0),
        overloaded: Number(summary?.overloaded ?? 0),
        optimal: Number(summary?.optimal ?? 0),
        available: Number(summary?.available ?? 0),
        avgUtilization: Number(summary?.avgUtilization ?? 0),
      },
    });
  },

  /**
   * Get teams for a department from backend.
   */
  async getDepartmentTeams(departmentId: string): Promise<ApiResponse<DepartmentTeamMetrics[]>> {
    const response = await apiClient.get(API_ENDPOINTS.DEPARTMENTS.TEAMS(departmentId));
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    const teams = Array.isArray(data)
      ? data.map(mapRawToTeamMetrics)
      : [];
    return wrapResponse(teams);
  },

  /**
   * Create a new department
   */
  async createDepartment(departmentData: {
    name: string;
    headId?: string;
    color?: string;
  }): Promise<DepartmentSummary> {
    const response = await apiClient.post(API_ENDPOINTS.DEPARTMENTS.LIST, departmentData);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToDepartmentSummary(data);
  },

  /**
   * Update an existing department
   */
  async updateDepartment(
    departmentId: string,
    departmentData: {
      name?: string;
      headId?: string;
      color?: string;
    }
  ): Promise<DepartmentSummary> {
    const response = await apiClient.put(
      `${API_ENDPOINTS.DEPARTMENTS.GET(departmentId)}`,
      departmentData
    );
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToDepartmentSummary(data);
  },

  /**
   * Delete a department
   */
  async deleteDepartment(departmentId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.DEPARTMENTS.GET(departmentId));
  },
};

export default departmentService;
